import React, { useEffect, useState } from 'react';
import CsvDownloader from 'react-csv-downloader';
import Datetime from 'react-datetime';
import ReactQuill from 'react-quill-new';
import {
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd';
import { get, omit } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import slugify from 'slugify';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import { PageSpinner } from 'components/page-spinner';
import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';

import CourierApi from 'services/courier';
import PromoApi from 'services/promos';
import WarehouseApi from 'services/warehouse';

import { errHandling } from 'utils';
import {
  percentageInputNumberFormatter,
  priceInputNumberFormatter,
  priceInputNumberParser,
} from 'utils/inputNumber';

import 'react-quill-new/dist/quill.snow.css';
import 'react-datetime/css/react-datetime.css';
import './style.scss';
import { useNavigate } from 'react-router-dom';

const { confirm } = Modal;
const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { Title } = Typography;

const FIELDS = {
  CODE: 'code',
  DESCRIPTION: 'description',
  DESKTOP_MEDIA_URL: 'image_url',
  END_DATE: 'end_date',
  LONGEVITY: 'longevity',
  MOBILE_MEDIA_URL: 'mobile_image',
  NEW_USER_VOUCHER: 'new_user',
  PRIVATE: 'private',
  PROMO_CODES_ATTRIBUTES: 'promo_codes_attributes',
  PROMO_RULES: 'promo_rules',
  QUOTA: 'quota',
  SINGLE_USE: 'single_use',
  START_DATE: 'start_date',
  TITLE: 'title',
  USER_TYPES: 'user_type',
};

const options = {
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  month: '2-digit',
  second: '2-digit',
  timeZone: 'Asia/Jakarta',
  timeZoneName: 'short',
  year: 'numeric',
};
const formatter = new Intl.DateTimeFormat('sv-SE', options);

const USER_TYPE_OPTIONS = [
  { label: 'Guest', value: 'guest' },
  { label: 'Customer', value: 'user' },
  { label: 'Reseller', value: 'business' },
];

export function PromoCreateEdit() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [addRulesForm, setAddRulesForm] = useState({});
  const [allProducts, setAllProducts] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [formLayout] = useState('vertical');
  const [infinityQuota, setInfinityQuota] = useState(false);
  const [isPromoPrivate, setIsPromoPrivate] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [modalPurpose, setModalPurpose] = useState('add');
  const [modalVisible, setModalVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [productModal, setProductModal] = useState(false);
  const [productNextPage, setProductNextPage] = useState(1);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoCodesDownloadable, setPromoCodesDownloadable] = useState(false);
  const [rulesList, setRulesList] = useState([
    'courier',
    'deduction',
    'group_product',
    'minimum_order',
    'shipping_deduction',
    'warehouse_origin',
    'cashback',
    'transport_deduction',
    'installation_deduction',
    'shipping_type',
  ]);
  const [searchValue, setSearchValue] = useState('');
  const [singleUsed, setSingleUsed] = useState(false);
  const [value, setValue] = useState({});
  const [warehouseList, setWarehouseList] = useState([]);

  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get('id');

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { span: 14, offset: 4 },
        }
      : null;

  const modules = {
    toolbar: false,
  };

  const formats = [
    'blockquote',
    'bold',
    'bullet',
    'header',
    'image',
    'indent',
    'italic',
    'link',
    'list',
    'strike',
    'underline',
  ];

  const handleDateTimeValidation = (type, current) => {
    const cur = dayjs(current);
    if (type === 'startTime') {
      if (
        get(value, FIELDS.END_DATE, '') !== '' &&
        get(value, FIELDS.END_DATE, '') !== null
      ) {
        const beforeEnd = dayjs(
          get(value, FIELDS.END_DATE, ''),
          'YYYY-MM-DD HH:mm'
        );
        return (
          cur.isBefore(beforeEnd) &&
          cur.isSameOrAfter(dayjs().startOf('day'))
        );
      } else {
        return cur.isSameOrAfter(dayjs().startOf('day'));
      }
    } else {
      if (
        get(value, FIELDS.START_DATE, '') !== '' &&
        get(value, FIELDS.START_DATE, '') !== null
      ) {
        const beforeStart = dayjs(
          get(value, FIELDS.START_DATE, ''),
          'YYYY-MM-DD HH:mm'
        );
        return cur.isSameOrAfter(beforeStart);
      } else {
        return cur.isSameOrAfter(dayjs().startOf('day'));
      }
    }
  };

  async function getPromo(id) {
    try {
      setLoadingPage(true);
      const { data } = await PromoApi.getPromo(id);
      let promoData = data.data;

      if (promoData[FIELDS.QUOTA] === null || promoData[FIELDS.QUOTA] < 0) {
        setInfinityQuota(true);
      }
      if (promoData[FIELDS.START_DATE]) {
        let newStartDate = formatter.format(
          new Date(promoData[FIELDS.START_DATE])
        );
        promoData[FIELDS.START_DATE] = newStartDate;
      }

      const mapCourier = promoData[FIELDS.PROMO_RULES].map((rule = {}) => {
        const { rule_type } = rule || {};
        if (rule_type === 'courier') {
          rule.courier_id = get(rule, ['courier', 'id'], null);
        }
        if (rule_type === 'warehouse_origin') {
          rule.warehouse_id = get(rule, ['warehouse', 'id'], null);
        }
        return rule;
      });

      promoData[FIELDS.PROMO_RULES] = mapCourier;

      if (promoData[FIELDS.END_DATE]) {
        let newEndDate = formatter.format(new Date(promoData[FIELDS.END_DATE]));
        promoData[FIELDS.END_DATE] = newEndDate;
      }

      if (promoData[FIELDS.SINGLE_USE]) {
        const {
          data: { data: promoCodes },
        } = await PromoApi.downloadSingleUsedCodes(id);
        setPromoCodes(promoCodes);
        setPromoCodesDownloadable(true);
        setSingleUsed(true);
      }
      if (promoData[FIELDS.PRIVATE]) {
        setIsPromoPrivate(true);
      }

      // modify warehouse in promodata
      setValue(promoData);
    } finally {
      setLoadingPage(false);
    }
  }

  async function getCouriers() {
    try {
      const { data } = await CourierApi.getCourierList();
      const CourierList = data.data;
      setCouriers(CourierList);
    } catch (err) {
      errHandling(err);
    }
  }

  async function getWarehouses() {
    try {
      const {
        data: { data = [] },
      } = await WarehouseApi.getWarehouseList({ status: 'active' });
      setWarehouseList(data);
    } catch (error) {
      errHandling(error);
    }
  }

  function hasErrors() {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  }

  async function handleSubmit(values) {
    try {
      delete values[FIELDS.START_DATE];
      delete values[FIELDS.END_DATE];
      if (infinityQuota) {
        values[FIELDS.QUOTA] = -1;
      }

      values[FIELDS.SINGLE_USE] = singleUsed;
      if (!singleUsed) {
        values[FIELDS.PROMO_CODES_ATTRIBUTES] = [
          {
            code: values[FIELDS.CODE],
            quota: values[FIELDS.QUOTA],
          },
        ];
      } else {
        values[FIELDS.PROMO_CODES_ATTRIBUTES] = [];
      }

      const promoRules = value[FIELDS.PROMO_RULES] || [];
      let mapPromoRules = promoRules.map((x) => {
        if (x.rule_type === 'group_product') {
          x.promo_products_attributes = x.products.map((product) => {
            product.product_id = product.id;
            return product;
          });
        }
        return x;
      });

      setLoadingButton(true);

      values['promo_rules_attributes'] = mapPromoRules;

      if (id) {
        values.weight = Number(values.weight) * 1000;
        await PromoApi.updatePromo(id, { ...value, ...values });
        message.success('Successfully updated promo');
      } else {
        await PromoApi.createPromo(
          omit({ ...value, ...values }, [FIELDS.CODE])
        );
        message.success('Successfully created promo');
      }
      navigate({
        pathname: '/promos',
      });
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(key, e) {
    if (e !== null && e !== undefined) {
      setValue((prevState) => ({
        ...prevState,
        [key]: e,
      }));
    }
  }

  function changeValueDate(e, key) {
    let newValue = e;
    if (newValue) {
      let newDate = formatter.format(new Date(newValue));
      newValue = newDate;
    }
    setValue((prevState) => ({
      ...prevState,
      [key]: newValue,
    }));
  }

  function handleDelete(i) {
    confirm({
      title: 'Are you sure delete this rule?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        let newRules = value[FIELDS.PROMO_RULES].slice();
        newRules.splice(i, 1);
        setValue((prevState) => ({
          ...prevState,
          [FIELDS.PROMO_RULES]: newRules,
        }));
      },
      onCancel() {},
    });
  }

  function liveUpdateRules(val, key, index) {
    if (val) {
      let newRules = value[FIELDS.PROMO_RULES].slice();
      newRules[index][key] = val;

      setValue((prevState) => ({
        ...prevState,
        [key]: newRules,
      }));
    }
  }

  function modalUpdateRules(val, key) {
    if (val) {
      let newRules = { ...addRulesForm };
      newRules[key] = val;
      setAddRulesForm(newRules);
    }
  }

  function handleVisibilityRadioGroupChange(e) {
    // TODO: Duality of state causes confusion
    setIsPromoPrivate(e.target.value);
    changeValue(FIELDS.PRIVATE, e.target.value);
    if (e.target.value) {
      setInfinityQuota(false);
    } else {
      setSingleUsed(false);
    }
  }

  function changeSearchValue(e) {
    setSearchValue(e.target.value);
  }

  function handleChangeValue(url, key) {
    setValue((prevState) => ({
      ...prevState,
      [key]: url,
    }));
  }

  function handleChangeValueDate(type, { _d }) {
    const current = dayjs(_d);
    if (!handleDateTimeValidation(type, current)) return;

    if (type === 'startTime') {
      changeValueDate(_d, FIELDS.START_DATE);
    } else {
      changeValueDate(_d, FIELDS.END_DATE);
    }
  }

  //for modal rule only
  const deductionForm = (defaultValue, onChange, index, label) => {
    return (
      <>
        <div>{label}</div>
        <div className="rule-form">
          <label title="Maximum promo amount">Maximum promo amount</label>
          <InputNumber
            className="inputForm input"
            value={defaultValue['deduction_max']}
            placeholder="Write maximum promo amount here"
            min={0}
            formatter={priceInputNumberFormatter}
            parser={priceInputNumberParser}
            step={1}
            onChange={(e) => onChange(e, 'deduction_max', index)}
          />
        </div>
        <div className="rule-form">
          <label title="Promo percentage">Promo percentage</label>
          <InputNumber
            className="inputForm input"
            value={defaultValue['deduction_percentage']}
            placeholder="Write percentage deduction here"
            min={0}
            max={100}
            formatter={(value) => `${value} %`}
            parser={priceInputNumberParser}
            step={1}
            onChange={(e) => onChange(e, 'deduction_percentage', index)}
          />
        </div>
      </>
    );
  };

  const minimumOrderForm = (defaultValue, onChange, index) => {
    return (
      <>
        <div className="rule-form">
          <label title="Minimum Order">Minimum Order</label>
          <InputNumber
            className="inputForm input"
            value={defaultValue['minimum_order']}
            placeholder="Write minimum order here"
            min={0}
            formatter={(value) =>
              `IDR ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={(value) => value.replace(/[^0-9]\s?|(\.*)/g, '')}
            step={1}
            onChange={(e) => onChange(e, 'minimum_order', index)}
          />
        </div>
      </>
    );
  };

  const courierForm = (defaultValue, onChange, index) => {
    return (
      <div className="rule-form">
        <label title="Courier">Courier</label>
        <Select
          className="inputForm input"
          value={Number(defaultValue['courier_id'])}
          onChange={(e) => onChange(e, 'courier_id', index)}>
          <Option value={0} disabled>
            Select Courier
          </Option>
          {couriers.map((courier, i) => (
            <Option key={i} value={courier.id}>
              {courier.title}
            </Option>
          ))}
        </Select>
      </div>
    );
  };

  const shippingDeductionForm = (defaultValue, onChange, index) => {
    return (
      <>
        <div className="rule-form">
          <label title="Maximum Free Shipping">Maximum Free Shipping</label>
          <InputNumber
            className="inputForm input"
            value={defaultValue['deduction_max']}
            formatter={priceInputNumberFormatter}
            parser={priceInputNumberParser}
            placeholder="Write maximum free shipping here"
            min={0}
            step={1}
            onChange={(e) => onChange(e, 'deduction_max', index)}
          />
        </div>
      </>
    );
  };

  const productForm = (defaultValue) => {
    if (!defaultValue.products) return <></>;
    return (
      <div className="rule-form">
        <label title="Courier">Products</label>
        {defaultValue.products.map((product, i) => (
          <div className="mt-1" key={i}>
            {product.id !== undefined ? (
              <Row align="middle" className="product-container" type="flex">
                <Col span={3}>
                  <img
                    alt="product rules"
                    className="product-img"
                    src={
                      get(product, 'preview.secure_url', '') === ''
                        ? `/img/logo/no-image.png`
                        : get(product, 'preview.secure_url', '')
                    }
                  />
                </Col>
                <Col span={16} className="product-title">
                  {product.title}
                </Col>
                <Col span={5}>
                  <PdButton full onClick={removeRulesProduct} type="danger">
                    Remove
                  </PdButton>
                </Col>
              </Row>
            ) : (
              <Row>
                <Col span={7}>
                  <PdButton
                    onClick={showModalProduct}
                    full
                    outline
                    type="primary">
                    Select Product
                  </PdButton>
                </Col>
                <Col span={5} offset={12}>
                  <PdButton full onClick={removeRulesProduct} type="danger">
                    Remove
                  </PdButton>
                </Col>
              </Row>
            )}
          </div>
        ))}
        <PdButton
          className="mt-40"
          full
          onClick={addProduct}
          outline
          type="primary">
          Add Product
        </PdButton>
      </div>
    );
  };

  const warehouseForm = (defaultValue, onChange, index) => {
    return (
      <div className="rule-form">
        <label title="Warehouse">Warehouse</label>
        <Select
          className="inputForm input"
          value={Number(defaultValue['warehouse_id'])}
          onChange={(e) => onChange(e, 'warehouse_id', index)}>
          <Option value={0} disabled>
            Select Warehouse
          </Option>
          {warehouseList.map(({ id, title }) => (
            <Option key={id} value={id}>
              {title}
            </Option>
          ))}
        </Select>
      </div>
    );
  };

  const cashbackForm = (defaultValue, onChange, index) => {
    return (
      <>
        <div className="rule-form">
          <label title="Maximum cashback amount">Maximum cashback amount</label>
          <InputNumber
            className="inputForm input"
            value={defaultValue['cashback_max']}
            placeholder="Write maximum cashback amount here"
            min={0}
            formatter={priceInputNumberFormatter}
            parser={priceInputNumberParser}
            step={1}
            onChange={(e) => onChange(e, 'cashback_max', index)}
          />
        </div>
        <div className="rule-form">
          <label title="Cashback percentage">Cashback percentage</label>
          <InputNumber
            className="inputForm input"
            value={defaultValue['cashback_percentage']}
            placeholder="Write percentage cashback here"
            min={0}
            max={100}
            formatter={(value) => `${value} %`}
            parser={priceInputNumberParser}
            step={1}
            onChange={(e) => onChange(e, 'cashback_percentage', index)}
          />
        </div>
      </>
    );
  };

  const installationRuleForm = (defaultValue, onChange, index, label) => {
    return (
      <>
        <div>{label}</div>
        <div className="rule-form">
          <label title="Maximum promo amount">Maximum promo amount</label>
          <InputNumber
            className="inputForm input"
            value={defaultValue['deduction_max']}
            placeholder="Write maximum promo amount here"
            min={0}
            formatter={priceInputNumberFormatter}
            parser={priceInputNumberParser}
            step={1}
            onChange={(e) => onChange(e, 'deduction_max', index)}
          />
        </div>
        <div className="rule-form">
          <label title="Promo percentage">Promo percentage</label>
          <InputNumber
            className="inputForm input"
            value={defaultValue['deduction_percentage']}
            placeholder="Write percentage deduction here"
            min={0}
            max={100}
            formatter={percentageInputNumberFormatter}
            parser={priceInputNumberParser}
            step={1}
            onChange={(e) => onChange(e, 'deduction_percentage', index)}
          />
        </div>
      </>
    );
  };

  const shippingForm = (defaultValue, onChange, index) => {
    return (
      <div className="rule-form">
        <label title="Warehouse">Shipping</label>
        <Select
          className="inputForm input"
          placeholder="Select shipping"
          defaultValue={defaultValue['shipping_type'] || undefined}
          onChange={(e) => onChange(e, 'shipping_type', index)}>
          {['Delivery', 'Pickup'].map((shipping) => (
            <Option key={shipping} value={shipping}>
              {shipping}
            </Option>
          ))}
        </Select>
      </div>
    );
  };

  const formList = {
    courier(defaultValue, onChange, index) {
      return courierForm(defaultValue, onChange, index);
    },
    deduction(defaultValue, onChange, index) {
      return deductionForm(defaultValue, onChange, index, 'Deduction');
    },
    minimum_order(defaultValue, onChange, index) {
      return minimumOrderForm(defaultValue, onChange, index);
    },
    shipping_deduction(defaultValue, onChange, index) {
      return shippingDeductionForm(defaultValue, onChange, index);
    },
    warehouse_origin(defaultValue, onChange, index) {
      return warehouseForm(defaultValue, onChange, index);
    },
    cashback(defaultValue, onChange, index) {
      return cashbackForm(defaultValue, onChange, index);
    },
    transport_deduction(defaultValue, onChange, index) {
      return installationRuleForm(
        defaultValue,
        onChange,
        index,
        'Transport Deduction'
      );
    },
    installation_deduction(defaultValue, onChange, index) {
      return installationRuleForm(
        defaultValue,
        onChange,
        index,
        'Installation Deduction'
      );
    },
    shipping_type(defaultValue, onChange, index) {
      return shippingForm(defaultValue, onChange, index);
    },
  };

  const addNewRuleFormCollections = {
    deduction: deductionForm(addRulesForm, modalUpdateRules),
    minimum_order: minimumOrderForm(addRulesForm, modalUpdateRules),
    courier: courierForm(addRulesForm, modalUpdateRules),
    shipping_deduction: shippingDeductionForm(addRulesForm, modalUpdateRules),
    group_product: productForm(addRulesForm),
    warehouse_origin: warehouseForm(addRulesForm, modalUpdateRules),
    cashback: cashbackForm(addRulesForm, modalUpdateRules),
    transport_deduction: installationRuleForm(addRulesForm, modalUpdateRules),
    installation_deduction: installationRuleForm(
      addRulesForm,
      modalUpdateRules
    ),
    shipping_type: shippingForm(addRulesForm, modalUpdateRules),
  };

  async function fetchAllProduct(
    page = 1,
    q = '',
    loadmore,
    total = 4,
    sort_by = 'created_at desc'
  ) {
    try {
      const { data } = await PromoApi.getProductList(page, q, sort_by, total);
      const productData = data.data;
      const metadata = data.metadata;

      const currentProduct = addRulesForm.products.slice();

      const filterProduct = productData.filter(
        (product) =>
          currentProduct.filter((e) => e.id === product.id).length === 0
      );

      setProductNextPage(metadata.next_cursor);
      if (loadmore) {
        setAllProducts((prevstate) => [...prevstate, ...filterProduct]);
      } else {
        setAllProducts(filterProduct);
      }
    } catch (err) {
      errHandling(err);
    }
  }

  async function handleDownloadCodesDetail(e) {
    e.preventDefault();
  }

  function showModal(purpose = 'add') {
    const initList = [
      'deduction',
      'minimum_order',
      'courier',
      'shipping_deduction',
      'group_product',
      'warehouse_origin',
      'cashback',
      'transport_deduction',
      'installation_deduction',
      'shipping_type',
    ];

    setModalPurpose(purpose);
    setRulesList(initList);
    setAddRulesForm({});
    setModalVisible(true);
  }

  function exitModal() {
    setModalVisible(false);
  }

  function addNewRule(type) {
    let newRules = get(value, FIELDS.PROMO_RULES, []);
    if (type === 'group_product') {
      const formData = { ...addRulesForm };
      const products = formData.products.slice();
      const filterProduct = products.filter((x) => x.id !== undefined);
      if (filterProduct.length > 0) {
        formData.products = filterProduct;
        newRules.push(formData);
        setValue((prevState) => ({
          ...prevState,
          [FIELDS.PROMO_RULES]: newRules,
        }));
        setModalVisible(false);
      } else {
        message.error('please select a product!');
      }
    } else {
      newRules.push(addRulesForm);
      setValue((prevState) => ({
        ...prevState,
        [FIELDS.PROMO_RULES]: newRules,
      }));
      setModalVisible(false);
    }
  }

  function saveNewRule(type) {
    const currentRules = get(value, FIELDS.PROMO_RULES, []);
    let newRules = currentRules.filter((x) => x.rule_type !== type);
    if (type === 'group_product') {
      const formData = { ...addRulesForm };
      const products = formData.products.slice();
      const filterProduct = products.filter((x) => x.id !== undefined);
      if (filterProduct.length > 0) {
        formData.products = filterProduct;
        newRules.push(formData);
        setValue((prevState) => ({
          ...prevState,
          [FIELDS.PROMO_RULES]: newRules,
        }));
        setModalVisible(false);
      } else {
        message.error('please select a product!');
      }
    } else {
      newRules.push(addRulesForm);
      setValue((prevState) => ({
        ...prevState,
        [FIELDS.PROMO_RULES]: newRules,
      }));
      setModalVisible(false);
    }
  }

  function typeName(type) {
    let str = type.split('_');
    str = str.map((item) => {
      return item.charAt(0).toUpperCase() + item.slice(1);
    });
    if (type === 'group_product') {
      return 'Selected Product';
    } else {
      return str.join(' ');
    }
  }

  function changeRuleType(val) {
    switch (val) {
      case 'deduction':
        setAddRulesForm({
          rule_type: 'deduction',
          deduction_max: 1000,
          deduction_percentage: 1,
        });
        break;

      case 'minimum_order':
        setAddRulesForm({
          rule_type: 'minimum_order',
          minimum_order: 1000,
        });
        break;

      case 'courier':
        setAddRulesForm({
          rule_type: 'courier',
          courier_id: 1,
        });
        break;

      case 'shipping_deduction':
        setAddRulesForm({
          rule_type: 'shipping_deduction',
          deduction_max: 1000,
        });
        break;

      case 'group_product':
        setAddRulesForm({
          rule_type: 'group_product',
          products: [{}],
        });
        break;

      case 'warehouse_origin':
        setAddRulesForm({
          rule_type: 'warehouse_origin',
          warehouse_id: 11,
        });
        break;

      case 'cashback':
        setAddRulesForm({
          rule_type: 'cashback',
          cashback_max: 1000,
          cashback_percentage: 1,
        });
        break;
      case 'transport_deduction':
        setAddRulesForm({
          rule_type: 'transport_deduction',
          deduction_max: 1000,
          deduction_percentage: 1,
        });
        break;
      case 'installation_deduction':
        setAddRulesForm({
          rule_type: 'installation_deduction',
          deduction_max: 1000,
          deduction_percentage: 1,
        });
        break;
      case 'shipping_type':
        setAddRulesForm({
          rule_type: 'shipping_type',
          shipping_type: 'Delivery',
        });
        break;
      default:
        setAddRulesForm({});
    }
  }

  function onCheckQuota(e) {
    setInfinityQuota(e.target.checked);
    form.setFieldsValue({
      [FIELDS.QUOTA]: null,
    });
  }

  function removeRulesProduct(index) {
    const products = addRulesForm.products.slice();
    products.splice(index, 1);
    modalUpdateRules(products, 'products');
  }

  function editSelectedProduct(index) {
    const rule = get(value, FIELDS.PROMO_RULES, []).slice();
    showModal('edit');
    setRulesList((prevState) => [...prevState, 'group_product']);
    setAddRulesForm({
      rule_type: 'group_product',
      products: [...rule[index].products],
    });
  }

  function addProduct() {
    const obj = { ...addRulesForm };
    const arr = [...obj.products];
    arr.push({});
    obj.products = arr;
    modalUpdateRules(arr, 'products');
  }

  function closeModalProduct() {
    setAllProducts([]);
    setProductModal(false);
    setPage(1);
  }

  function changePage(page) {
    setPage(page);
    fetchAllProduct(page, searchValue, true);
  }

  function searchOnClick() {
    fetchAllProduct(1, searchValue);
  }

  function showModalProduct() {
    setProductModal(true);
    fetchAllProduct();
  }

  function checkProduct(value) {
    const currentProducts = addRulesForm.products.slice();
    let indicator = false;
    const newProducts = currentProducts.map((x) => {
      if (x.id === undefined && !indicator) {
        indicator = true;
        return value;
      } else {
        return x;
      }
    });
    modalUpdateRules(newProducts, 'products');
    setAllProducts([]);
    setProductModal(false);
    setPage(1);
  }

  function onCheckGenerateSingleCode(e) {
    setSingleUsed(e.target.checked);
    form.setFieldsValue({
      [FIELDS.CODE]: null,
    });
  }

  function handleOnClick() {
    modalPurpose === 'edit'
      ? saveNewRule(get(addRulesForm, 'rule_type', ''))
      : addNewRule(get(addRulesForm, 'rule_type', ''));
  }

  useEffect(() => {
    getCouriers();
    getWarehouses();
  }, []);

  useEffect(() => {
    if (id) {
      getPromo(id);
    } else {
      setValue({});
    }
  }, [id]);

  // Update form fields when value changes
  useEffect(() => {
    form.setFieldsValue({
      [FIELDS.TITLE]: value[FIELDS.TITLE],
      [FIELDS.QUOTA]:
        get(value, FIELDS.QUOTA, null) !== null &&
        Number(get(value, FIELDS.QUOTA, null)) >= 0
          ? Number(get(value, FIELDS.QUOTA, 0))
          : '',
      [FIELDS.PRIVATE]: value[FIELDS.PRIVATE] || isPromoPrivate,
      [FIELDS.USER_TYPES]: value[FIELDS.USER_TYPES],
      [FIELDS.NEW_USER_VOUCHER]: value[FIELDS.NEW_USER_VOUCHER],
      [FIELDS.LONGEVITY]: value[FIELDS.LONGEVITY],
      [FIELDS.CODE]: value[FIELDS.CODE],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isPromoPrivate]);

  if (loadingPage) {
    return <PageSpinner />;
  }

  return (
    <Layout className="pd-cms-promo main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">{id ? 'Edit' : 'Create New'} Promo</div>
          <Content className="pageContent">
            <Form form={form} layout={formLayout} onFinish={handleSubmit}>
              <div className="section-container">
                <div className="inner-section">
                  <Form.Item
                    label="Title"
                    {...formItemLayout}
                    name={FIELDS.TITLE}
                    rules={[
                      { message: 'Please input Title!', required: true },
                    ]}>
                    <Input
                      onChange={(e) =>
                        changeValue(FIELDS.TITLE, e.target.value)
                      }
                      placeholder="Enter promo title"
                    />
                  </Form.Item>

                  <Row gutter={20}>
                    <Col span={12}>
                      <Form.Item
                        label="Quota"
                        {...formItemLayout}
                        name={FIELDS.QUOTA}
                        rules={[
                          {
                            message: 'Please input Quota!',
                            required: !infinityQuota,
                          },
                        ]}>
                        <InputNumber
                          className="inputForm"
                          disabled={infinityQuota || id}
                          min={0}
                          onChange={(e) => changeValue(FIELDS.QUOTA, e)}
                          placeholder="Enter quota"
                          step="1"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      span={12}
                      className={isPromoPrivate ? 'display-none' : ''}>
                      <div className="infinity-container">
                        <Checkbox
                          checked={infinityQuota}
                          disabled={id}
                          onChange={onCheckQuota}
                        />
                        <span className="text">Unlimited</span>
                      </div>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Visibility"
                    {...formItemLayout}
                    name={FIELDS.PRIVATE}
                    rules={[
                      {
                        message: 'Please select visibility!',
                        required: true,
                      },
                    ]}>
                    <Radio.Group
                      disabled={id || value[FIELDS.NEW_USER_VOUCHER]}
                      onChange={handleVisibilityRadioGroupChange}>
                      <Radio value={true}>Private</Radio>
                      <Radio value={false}>Public</Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item
                    label="User Type"
                    name={FIELDS.USER_TYPES}
                    rules={[
                      {
                        message: 'Please select user types!',
                        required: true,
                      },
                    ]}>
                    <Checkbox.Group
                      options={USER_TYPE_OPTIONS.map((data) => ({
                        ...data,
                        disabled:
                          data.value === 'guest'
                            ? value[FIELDS.NEW_USER_VOUCHER]
                            : false,
                      }))}
                      onChange={(e) => changeValue(FIELDS.USER_TYPES, e)}
                    />
                  </Form.Item>

                  <Form.Item
                    name={FIELDS.NEW_USER_VOUCHER}
                    valuePropName="checked">
                    <Checkbox
                      onChange={(event) => {
                        form.setFieldsValue({
                          [FIELDS.USER_TYPES]: form
                            .getFieldValue(FIELDS.USER_TYPES)
                            .filter((x) => x !== 'guest'),
                        });
                        setValue((prev) => ({
                          ...prev,
                          [FIELDS.NEW_USER_VOUCHER]: event.target.checked,
                          [FIELDS.PRIVATE]: !event.target.checked,
                          [FIELDS.USER_TYPES]: form
                            .getFieldValue(FIELDS.USER_TYPES)
                            .filter((x) => x !== 'guest'),
                        }));
                      }}
                      checked={Boolean(value[FIELDS.NEW_USER_VOUCHER])}
                      disabled={
                        Boolean(id) ||
                        (form.getFieldValue(FIELDS.USER_TYPES) || []).includes(
                          'guest'
                        )
                      }>
                      New User Voucher
                    </Checkbox>
                  </Form.Item>

                  {Boolean(value[FIELDS.NEW_USER_VOUCHER]) && (
                    <Row>
                      <Col span={12}>
                        <Form.Item
                          name={FIELDS.LONGEVITY}
                          label="Longevity"
                          rules={[
                            {
                              message: 'Please fill longevity',
                              required: true,
                            },
                          ]}>
                          <InputNumber
                            className="inputForm input"
                            placeholder="Write the longevity here"
                            min={0}
                            formatter={(value) =>
                              `${value} ${+value > 1 ? 'Days' : 'Day'}`
                            }
                            parser={priceInputNumberParser}
                            step={1}
                            onChange={(e) => changeValue(FIELDS.LONGEVITY, e)}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  <Row gutter={20} className="pd-margin-bottom-md">
                    <Col lg={12}>
                      <Form.Item
                        label="Code"
                        name={FIELDS.CODE}
                        {...formItemLayout}
                        rules={[
                          {
                            message: 'Please input promo code!',
                            required: !singleUsed,
                          },
                        ]}
                        normalize={(value) =>
                          value &&
                          slugify(value.toUpperCase(), {
                            remove: /\W/g,
                          })
                        }>
                        <Input
                          disabled={(singleUsed && isPromoPrivate) || id}
                          onChange={(e) => changeValue(FIELDS.CODE, e.target.value)}
                          placeholder="Enter promo code"
                        />
                      </Form.Item>
                    </Col>
                    {isPromoPrivate && (
                      <Col lg={12}>
                        <div className="infinity-container">
                          <Checkbox
                            checked={singleUsed}
                            disabled={id}
                            onChange={onCheckGenerateSingleCode}>
                            Generate random single used
                          </Checkbox>
                        </div>
                      </Col>
                    )}
                    {isPromoPrivate && singleUsed && (
                      <Col lg={24}>
                        <CsvDownloader
                          datas={promoCodes}
                          filename="single-use-promo-codes"
                          separator=",">
                          <button
                            className="link"
                            disabled={!promoCodesDownloadable}
                            onClick={handleDownloadCodesDetail}>
                            Download single-used codes detail
                          </button>
                        </CsvDownloader>
                      </Col>
                    )}
                  </Row>

                  <Form.Item label="Image (490x140 px)" {...formItemLayout}>
                    <PdUploader
                      afterUpload={handleChangeValue}
                      imgClassName="w-70"
                      imgUrl={value[FIELDS.DESKTOP_MEDIA_URL]}
                      keyValue={FIELDS.DESKTOP_MEDIA_URL}
                    />
                  </Form.Item>

                  <Row gutter={20}>
                    <Col span={12}>
                      <Form.Item
                        label="Start Time (in WIB)"
                        {...formItemLayout}>
                        <Datetime
                          value={
                            get(value, FIELDS.START_DATE, '')
                              ? dayjs(
                                  get(value, FIELDS.START_DATE, '')
                                ).toDate()
                              : ''
                          }
                          inputProps={{
                            className: 'ant-input',
                          }}
                          isValidDate={handleDateTimeValidation.bind(
                            this,
                            'startTime'
                          )}
                          onChange={handleChangeValueDate.bind(
                            this,
                            'startTime'
                          )}
                          timeFormat="HH:mm"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="End Time (in WIB)" {...formItemLayout}>
                        <Datetime
                          value={
                            get(value, FIELDS.END_DATE, '')
                              ? dayjs(get(value, FIELDS.END_DATE, '')).toDate()
                              : ''
                          }
                          inputProps={{
                            className: 'ant-input',
                          }}
                          isValidDate={handleDateTimeValidation.bind(
                            this,
                            'endTime'
                          )}
                          onChange={handleChangeValueDate.bind(this, 'endTime')}
                          timeFormat="HH:mm"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item label="Description" {...formItemLayout}>
                    <ReactQuill
                      defaultValue={get(value, FIELDS.DESCRIPTION, '')}
                      formats={formats}
                      modules={modules}
                      onChange={changeValue.bind(this, FIELDS.DESCRIPTION)}
                      placeholder="Enter description"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="section-container">
                <div className="inner-section">
                  <Row gutter={20}>
                    <Col span={20}>
                      <Title className="pageSubtitle" level={3}>
                        Rules
                      </Title>
                    </Col>
                    <Col span={4}>
                      {get(value, FIELDS.PROMO_RULES, []).length < 5 && (
                        <PdButton full onClick={showModal} type="primary">
                          Add Rules
                        </PdButton>
                      )}
                    </Col>

                    <Col className="pd-cms-promo--rule-list" span={24}>
                      {get(value, FIELDS.PROMO_RULES, []).map((promo, i) => {
                        if (!promo) {
                          return null;
                        }
                        return (
                          <Row
                            align="middle"
                            className="rule-row"
                            gutter={20}
                            key={i}
                            type="flex">
                            {promo.rule_type === 'group_product' ? (
                              <>
                                <Col span={16} className="rule-form">
                                  <label title="Maximum promo amount">
                                    Selected Products
                                  </label>
                                  <div className="mt-1">
                                    {`${promo.products.length} product(s) Selected`}
                                  </div>
                                </Col>
                                <Col span={4}>
                                  <PdButton
                                    full
                                    onClick={editSelectedProduct.bind(this, i)}
                                    outline
                                    type="primary">
                                    Edit
                                  </PdButton>
                                </Col>
                                <Col span={4}>
                                  <PdButton
                                    full
                                    onClick={handleDelete.bind(this, i)}
                                    outline
                                    type="danger">
                                    Delete
                                  </PdButton>
                                </Col>
                              </>
                            ) : (
                              <>
                                <Col span={17}>
                                  {formList[promo.rule_type](
                                    value[FIELDS.PROMO_RULES][i],
                                    liveUpdateRules,
                                    i
                                  )}
                                </Col>

                                <Col span={4} offset={3}>
                                  <PdButton
                                    onClick={handleDelete.bind(this, i)}
                                    full
                                    outline
                                    type="danger">
                                    Delete
                                  </PdButton>
                                </Col>
                              </>
                            )}
                          </Row>
                        );
                      })}
                    </Col>
                  </Row>
                </div>
                <Form.Item {...buttonItemLayout}>
                  <div className="buttonSection mt-40">
                    <PdButton
                      disabled={hasErrors()}
                      full
                      htmlType="submit"
                      loading={loadingButton}
                      type="primary">
                      Publish
                    </PdButton>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </Content>
        </div>
      </div>
      <Modal
        centered
        className="modal--productList"
        onCancel={exitModal}
        onOk={exitModal}
        open={modalVisible}
        wrapClassName="hidden-wrap-modal">
        <div className="modal-container">
          <Row>
            <Col className="header" span={24}>
              <h1 className="bold">
                {modalPurpose === 'edit' ? 'Edit' : 'Add'} Rule
              </h1>
            </Col>
            <Col className="form-container" span={24}>
              <div className="rule-form">
                <label title="Tyype">Type</label>
                <Select
                  className="inputForm input"
                  disabled={modalPurpose === 'edit'}
                  onChange={changeRuleType}
                  placeholder="Select rule type"
                  value={get(addRulesForm, 'rule_type', '')}>
                  <Option disabled value="">
                    Select Rule type
                  </Option>
                  {rulesList.map((type, i) => (
                    <Option key={i} value={type}>
                      {typeName(type)}
                    </Option>
                  ))}
                </Select>
              </div>
              {addNewRuleFormCollections[get(addRulesForm, 'rule_type', '')]}
            </Col>
            <Col sm={{ offset: 7, span: 10 }}>
              {get(addRulesForm, 'rule_type', '') !== '' && (
                <PdButton full onClick={handleOnClick} type="primary">
                  {modalPurpose === 'edit' ? 'Save' : 'Add'} Rule
                </PdButton>
              )}
            </Col>
          </Row>
        </div>
      </Modal>
      <Modal
        className="modal--productSizeList"
        onCancel={closeModalProduct}
        onOk={setProductModal.bind(this, false)}
        title="Select Product"
        open={productModal}>
        <div className="modal-container">
          <Row>
            <Col span={24}>
              <Search
                className="search-product"
                enterButton
                onChange={changeSearchValue}
                onSearch={searchOnClick}
                placeholder="Search Product List"
                value={searchValue}
              />
            </Col>
            <Col span={24} className="product-catalog">
              {allProducts.map((product, i) => (
                <Row key={i} className="product-item">
                  <Col span={1}>
                    <Checkbox
                      checked={product.checked}
                      onChange={checkProduct.bind(this, product)}
                    />
                  </Col>
                  <Col span={23} className="product-title">
                    <span className="bold">{product.title}</span>
                  </Col>
                </Row>
              ))}
              <Row>
                <Col sm={{ offset: 8, span: 8 }}>
                  <div className="center-button">
                    {productNextPage !== -1 && (
                      <PdButton
                        className="custom-button"
                        onClick={changePage.bind(this, Number(page) + 1)}
                        outline
                        type="primary">
                        Load More
                      </PdButton>
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Modal>
    </Layout>
  );
}

export default PromoCreateEdit;
